import {
  getLeadId,
  getSessionId,
  setCustomerId,
  setLeadId,
  setSessionId,
} from "../storage/customer";
import { pushFormEvent } from "../gtm/data-layer";
import { getConfig } from "../config";
import type {
  RegisterLeadPayload,
  RegisterLeadResult,
  RegisterLeadOptions,
  UpdateLeadPayload,
  UpdateLeadResult,
  UpdateLeadOptions,
} from "../types";

export async function registerLead(
  data: RegisterLeadPayload,
  options?: RegisterLeadOptions,
): Promise<RegisterLeadResult> {
  const { apiBaseUrl } = getConfig();
  const { formName, eventName, callback, prefix } = options ?? {};

  try {
    const response = await fetch(`${apiBaseUrl}/public/leads`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      const result: RegisterLeadResult = {
        success: false,
        error:
          (body as { message?: string }).message ||
          `Request failed with status ${response.status}`,
      };
      callback?.(result);
      return result;
    }

    const body = await response.json();
    const sessionId = response.headers.get("x-session-id");

    const leadResponse = body as {
      id: string;
      leads?: { id: string; customer_id: string }[];
    };
    const customerId = leadResponse.id;
    const leadId = leadResponse.leads?.[0]?.id;

    if (leadId) setLeadId(leadId, prefix);
    if (customerId) setCustomerId(customerId, prefix);
    if (sessionId) setSessionId(sessionId, prefix);

    pushFormEvent(formName, eventName, prefix);

    const result: RegisterLeadResult = {
      success: true,
      leadId,
      customerId,
    };
    callback?.(result);
    return result;
  } catch (err) {
    const result: RegisterLeadResult = {
      success: false,
      error: err instanceof Error ? err.message : "Unknown error",
    };
    callback?.(result);
    return result;
  }
}

export async function updateLead(
  data: UpdateLeadPayload,
  options?: UpdateLeadOptions,
): Promise<UpdateLeadResult> {
  const { apiBaseUrl } = getConfig();
  const { prefix } = options ?? {};
  const leadId = getLeadId(prefix);
  const sessionId = getSessionId(prefix);

  if (!leadId || !sessionId) {
    return {
      success: false,
      error: "No active lead session. Call registerLead() first.",
    };
  }

  try {
    const response = await fetch(`${apiBaseUrl}/leads/${leadId}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        "x-session-id": sessionId,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      return {
        success: false,
        error:
          (body as { message?: string }).message ||
          `Request failed with status ${response.status}`,
      };
    }

    return { success: true };
  } catch (err) {
    return {
      success: false,
      error: err instanceof Error ? err.message : "Unknown error",
    };
  }
}
